import { useReducer, createContext } from 'react';
import { viewTypeList, scaleList, timePeriodList, deathTypeList, measurmentTypeList, getViewTypeObject, getScaleObject, getMeasurmentTypeObject, getDeathTypeObject } from './StaticVariables';
import { getAddressCandidateScale } from '../requests/OneMapSearchRequest';
import _defaultFeature from './DefaultFeature.json';
import defaultSearchResult from './DefaultSearchResult.json';
import { generateMapAttribute, generateDisplayAttribute, standardizeObjectKeys, geometryScaleFromGeoid } from '../utils/DataTransformation';
export const InterfaceContext = createContext();

const defaultFeature = standardizeObjectKeys(_defaultFeature);

//ΔVΔVΔ: UI Context Variables
const interface_init = {
    //Dropdowns
    activeViewType: viewTypeList[0].slug,
    activeScale: scaleList[0].slug,
    activeTimePeriod: timePeriodList[0].slug,
    activeDeathType: deathTypeList[0].slug,
    activeMeasurement: measurmentTypeList[0].slug,
    activeMapAttribute: generateMapAttribute({ type: viewTypeList[0].slug, deathType: deathTypeList[0].slug, measurement: measurmentTypeList[0].slug }),
    activeDataAttribute: generateDisplayAttribute({ type: viewTypeList[0].slug, deathType: deathTypeList[0].slug, measurement: measurmentTypeList[0].slug }),
    mapReadOnlyZoom: 0,
    error: false,
    recentError: null,
    errorLocation: '---',
    searchResult: defaultSearchResult,
    token: '',
    //Selected Feature
    selectedFeature: standardizeObjectKeys(defaultFeature),
    //Hovered Feature
    hoveredFeature: standardizeObjectKeys(defaultFeature),
    activePast12Months: '',
    nationalData: [],
    past12Months: {
        ed: {
            endTime: 0,
            startDateString: '',
            endDateString: ''
        },
        all: {
            endTime: 0,
            startDateString: '',
            endDateString: ''
        },
        homicide: {
            endTime: 0,
            startDateString: '',
            endDateString: ''
        },
        suicide: {
            endTime: 0,
            startDateString: '',
            endDateString: ''
        }
    }
};
// ΔVΔVΔ: UI Context Methods
const uiActions = (state, dispatch) => {
    return {
        setActiveViewType: (value) => dispatch({ type: 'setActiveViewType', value }),
        setActiveScale: (value) => dispatch({ type: 'setActiveScale', value }),
        setActiveTimePeriod: (value) => dispatch({ type: 'setActiveTimePeriod', value }),
        setActiveDeathType: (value) => dispatch({ type: 'setActiveDeathType', value }),
        setActiveMeasurement: (value) => dispatch({ type: 'setActiveMeasurement', value }),
        setSearchResult: (value) => dispatch({ type: 'setSearchResult', value }),
        setSelectedFeature: (obj) => dispatch({ type: 'setSelectedFeature', obj }),
        setHoveredFeature: (obj) => dispatch({ type: 'setHoveredFeature', obj }),
        setMapReadOnlyZoom: (value) => dispatch({ type: 'setMapReadOnlyZoom', value }),
        setAppError: (value, from = '', error) => dispatch({ type: 'setAppError', value, from, error }),
        setPast12Months: (value) => dispatch({ type: 'setPast12Months', value }),
        setNationalData: (value) => dispatch({ type: 'setNationalData', value }),
        setToken: (value) => dispatch({ type: 'setToken', value })
    };
};

// ΔVΔVΔ: UI Context State Transformation
const interfaceReducer = (state, action) => {
    let mapSlug, dataSlug, measurement, past12MonthsString, correctedFeature, correctedScale, correctedTimePeriod, featureScale;
    switch (action.type) {
        case 'setActiveScale':
            return { ...state, activeScale: action.value };
        case 'setActiveTimePeriod':
            correctedScale = validScaleByTime(action.value, state.activeScale);
            featureScale = geometryScaleFromGeoid(state.selectedFeature.GEOID);
            correctedFeature = correctedScale === featureScale || featureScale === 'us' ? state.selectedFeature : defaultFeature;
            return { ...state, activeTimePeriod: action.value, activeScale: validScaleByTime(action.value, state.activeScale), selectedFeature: correctedFeature };
        case 'setActiveViewType':
            measurement = validMeasurment(state.activeMeasurement, { ...state, activeViewType: action.value });
            mapSlug = generateMapAttribute({
                type: action.value,
                deathType: state.activeDeathType,
                measurement: state.activeMeasurement
            });
            dataSlug = generateDisplayAttribute({
                type: action.value,
                deathType: state.activeDeathType,
                measurement: state.activeMeasurement
            });
            past12MonthsString = getActivePast12MonthString({
                type: action.value,
                deathType: state.activeDeathType,
                data: state.past12Months
            });
            correctedTimePeriod = validTimePeriodByView(action.value, state);
            correctedScale = validViewTypeScale(action.value, state);
            correctedFeature = correctedScale === state.activeScale ? state.selectedFeature : defaultFeature;
            //only check that selected feature has the selected map attribute if the time period hasn't changed, otherwise the snapshot will trigger a new lookup
            if (correctedTimePeriod === state.activeTimePeriod && correctedFeature.GEOID === state.selectedFeature.GEOID && !featureHasAttribute(state.selectedFeature, mapSlug)) {
                correctedFeature = defaultFeature;
            }
            return { ...state, activeViewType: action.value, activeScale: correctedScale, activeMeasurement: measurement, activeMapAttribute: mapSlug, activeDataAttribute: dataSlug, activeTimePeriod: correctedTimePeriod, selectedFeature: correctedFeature, activePast12Months: past12MonthsString };
        // return { ...state, activeViewType: action.value, activeScale: validViewTypeScale(action.value, state), activeMeasurement: measurement, activeMapAttribute: mapSlug };
        case 'setActiveDeathType':
            mapSlug = generateMapAttribute({
                type: state.activeViewType,
                deathType: action.value,
                measurement: state.activeMeasurement
            });
            dataSlug = generateDisplayAttribute({
                type: state.activeViewType,
                deathType: action.value,
                measurement: state.activeMeasurement
            });
            past12MonthsString = getActivePast12MonthString({
                type: state.activeViewType,
                deathType: action.value,
                data: state.past12Months
            });
            correctedFeature = featureHasAttribute(state.selectedFeature, mapSlug) ? state.selectedFeature : defaultFeature;
            return { ...state, activeDeathType: action.value, activeMapAttribute: mapSlug, activeDataAttribute: dataSlug, selectedFeature: correctedFeature, activePast12Months: past12MonthsString };
        case 'setActiveMeasurement':
            mapSlug = generateMapAttribute({
                type: state.activeViewType,
                deathType: state.activeDeathType,
                measurement: action.value
            });
            dataSlug = generateDisplayAttribute({
                type: state.activeViewType,
                deathType: state.activeDeathType,
                measurement: action.value
            });
            correctedFeature = featureHasAttribute(state.selectedFeature, mapSlug) ? state.selectedFeature : defaultFeature;
            return { ...state, activeMeasurement: action.value, activeMapAttribute: mapSlug, activeDataAttribute: dataSlug, selectedFeature: correctedFeature };
        case 'setSearchResult':
            return { ...state, searchResult: action.value || defaultSearchResult, activeScale: validSearchScale(action.value, state) };
        case 'setSelectedFeature':
            return { ...state, selectedFeature: validFeature(action.obj, state.activeMapAttribute) };
        case 'setHoveredFeature':
            return { ...state, hoveredFeature: validFeature(action.obj, state.activeMapAttribute) };
        case 'setMapReadOnlyZoom':
            return { ...state, mapReadOnlyZoom: action.value };
        case 'setToken':
            return { ...state, token: action.value };
        case 'setPast12Months':
            past12MonthsString = getActivePast12MonthString({
                type: state.activeViewType,
                deathType: state.activeDeathType,
                data: action.value
            });
            return { ...state, past12Months: action.value, activePast12Months: past12MonthsString };
        case 'setNationalData':
            return { ...state, nationalData: action.value };
        case 'setAppError':
            console.log('@@@setting app error', action.value, action.from);
            return { ...state, error: action.value, recentError: action.error, errorLocation: action.value ? action.from : '' };
        default:
            return state;
    }
};

const getActivePast12MonthString = ({ type, deathType, data }) => {
    const pastDataSlug = type === 'deaths' ? deathType : 'ed';
    return data[pastDataSlug].startDateString + ' – ' + data[pastDataSlug].endDateString;
};

const InterfaceProvider = ({ children }) => {
    const [state, dispatch] = useReducer(interfaceReducer, interface_init);
    const value = {
        ...state,
        ...uiActions(state, dispatch)
    };
    return <InterfaceContext.Provider value={value}>{children}</InterfaceContext.Provider>;
};
export { InterfaceProvider };

const withinYearRequirment = (time, yearString) => {
    return yearString === '*' || yearString.split(',').indexOf(time) != -1;
};
const featureHasAttribute = (feature, mapAttribute) => {
    if (!mapAttribute) return false;
    const slug = mapAttribute.toLowerCase();
    return slug in feature && feature[slug] !== null && feature[slug] !== undefined;
};
const validFeature = (obj, mapAttribute) => {
    let standardizedObj = obj?.GEOID || obj?.geoid ? standardizeObjectKeys(obj) : false;
    if (!standardizedObj) return defaultFeature;
    let hasAttribute = featureHasAttribute(standardizedObj, mapAttribute);
    if (!hasAttribute) return defaultFeature;
    return standardizedObj;
};
const validScaleByZoom = (zoom, state) => {
    if (getScaleObject(state.activeScale).minZoom <= zoom) return state.activeScale;
    let validScales = getViewTypeObject(state.activeViewType).scales.filter((scaleslug) => {
        return getScaleObject(scaleslug).minZoom <= zoom;
    });
    return validScales[validScales.length - 1];
};
export const validScaleByTime = (time, scale) => {
    const scaleObj = getScaleObject(scale);
    if (!withinYearRequirment(time, scaleObj.years)) return 'county';
    return scale;
};
const validViewTypeScale = (view, state) => {
    let validScales = getViewTypeObject(view).scales;
    return validScales.indexOf(state.activeScale) !== -1 ? state.activeScale : validScales[validScales.length - 1];
};
const validMeasurment = (measurement, state) => {
    if (state.activeViewType === 'ed_visits' && measurement === 'count') return 'rate';
    return measurement;
};
const validSearchScale = (candidate, state) => {
    return state.activeScale;
    // if (!candidate) return state.activeScale;
    // let scaleFromSearch = getAddressCandidateScale(candidate, state.activeViewType, state.activeTimePeriod);
    // if (!scaleFromSearch) return state.activeScale;
    // let validScales = getViewTypeObject(state.activeViewType).scales;
    // return validScales.indexOf(scaleFromSearch) !== -1 ? scaleFromSearch : state.activeScale;
};
const validTimePeriodByView = (view, state) => {
    const viewObj = getViewTypeObject(view);
    if (withinYearRequirment(state.activeTimePeriod, viewObj.years)) return state.activeTimePeriod;
    return 'ttm';
};
